import type { IDropdownMenuItem, TSidebarVariant } from './types';
import { getRoutePath } from '@/routes/util';
import i18n from '@/shared/i18n';

export const DOCUMENTATION_URL = 'https://docs.kognitos.com';

/**
 * The URL of the single menu item that should read as selected, or undefined
 * when the current route belongs to none of them.
 *
 * The most specific match wins, comparing paths only (an item's query string
 * picks a view, not a different page). External links and disabled entries
 * never win, since neither corresponds to a route the user can be on.
 *
 * @param items - The menu's items.
 * @param pathname - The current location's pathname.
 * @returns The winning item's URL, if any.
 */
export const getActiveUrl = (
  items: IDropdownMenuItem[],
  pathname: string,
): string | undefined =>
  items
    .flatMap(item =>
      item.url && !item.isExternalUrl && !item.disabled ? [item.url] : [],
    )
    .filter(url => {
      const path = url.split('?')[0];
      return pathname === path || pathname.startsWith(`${path}/`);
    })
    .sort((a, b) => b.length - a.length)[0];

/**
 * The audit destination, which is workspace-scoped and separately named in a
 * finance workspace ("Audit") and org-scoped everywhere else ("Audit log").
 */
const getAuditMenuItem = ({
  variant,
  currentOrganizationId,
  currentWorkspaceId,
  onSelect,
}: {
  variant: TSidebarVariant;
  currentOrganizationId: string | null;
  currentWorkspaceId: string | null;
  onSelect?: () => void;
}): IDropdownMenuItem => {
  if (variant === 'finance') {
    const hasWorkspace = !!currentOrganizationId && !!currentWorkspaceId;
    return {
      key: 'audit',
      title: i18n.t('finance.nav.audit', { ns: 'finance' }),
      icon: 'FileText',
      url: hasWorkspace
        ? getRoutePath('FINANCE_AUDIT', {
            orgId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
          })
        : undefined,
      disabled: !hasWorkspace,
      onClick: onSelect,
    };
  }

  return {
    key: 'audit',
    title: i18n.t('auditLog.title', { ns: 'audit-log' }),
    icon: 'FileText',
    url: currentOrganizationId
      ? getRoutePath('AUDIT_LOG', { orgId: currentOrganizationId })
      : undefined,
    disabled: !currentOrganizationId,
    onClick: onSelect,
  };
};

const getRolesAndAccessMenuItem = ({
  currentOrganizationId,
  currentWorkspaceId,
  onSelect,
}: {
  currentOrganizationId: string | null;
  currentWorkspaceId: string | null;
  onSelect?: () => void;
}): IDropdownMenuItem => {
  const path = currentOrganizationId
    ? getRoutePath('ROLES_AND_ACCESS', { orgId: currentOrganizationId })
    : undefined;
  return {
    key: 'roles-and-access',
    title: i18n.t('navigation.rolesAndAccess', { ns: 'navigation' }),
    icon: 'ShieldCheck',
    url:
      path && currentWorkspaceId
        ? `${path}?scope=${encodeURIComponent(currentWorkspaceId)}`
        : path,
    disabled: !currentOrganizationId,
    onClick: onSelect,
  };
};

/**
 * The user dropdown's items, in render order. `Appearance` is not in this list:
 * it is an expandable submenu owned by `UserDropdown`, which inserts it
 * directly above `documentation`.
 *
 * @param options.variant - Which sidebar variant the workspace renders.
 * @param options.openHelp - Opens the support messenger.
 * @param options.isHelpConfigured - False when support chat has no app id here.
 * @param options.canAccessOrganizationSettings - False for users without
 *   org-level access; the Organization settings entry is left out for them.
 * @returns The dropdown items for this workspace and user.
 */
export const getUserMenuItems = ({
  variant,
  currentOrganizationId,
  currentWorkspaceId,
  isCurrentUserError,
  onSelect,
  logout,
  openHelp,
  isHelpConfigured,
  canAccessOrganizationSettings,
}: {
  variant: TSidebarVariant;
  currentOrganizationId: string | null;
  currentWorkspaceId: string | null;
  isCurrentUserError: boolean;
  onSelect?: () => void;
  logout: () => void;
  openHelp: () => void;
  isHelpConfigured: boolean;
  canAccessOrganizationSettings: boolean;
}): IDropdownMenuItem[] => {
  return [
    {
      key: 'performance',
      title: i18n.t('organization.performance.title', { ns: 'organization' }),
      icon: 'LineChart',
      url: currentOrganizationId
        ? getRoutePath('ORGANIZATION_PERFORMANCE', {
            orgId: currentOrganizationId,
          })
        : undefined,
      disabled: !currentOrganizationId,
      onClick: onSelect,
    },
    {
      key: 'consumption',
      title: i18n.t('organization.consumption.title', {
        ns: 'organization',
      }),
      icon: 'BarChart3',
      url: currentOrganizationId
        ? getRoutePath('ORGANIZATION_CONSUMPTION', {
            orgId: currentOrganizationId,
          })
        : undefined,
      disabled: !currentOrganizationId,
      onClick: onSelect,
    },
    getAuditMenuItem({
      variant,
      currentOrganizationId,
      currentWorkspaceId,
      onSelect,
    }),
    {
      key: 'separator-1',
      type: 'separator',
    },
    getRolesAndAccessMenuItem({
      currentOrganizationId,
      currentWorkspaceId,
      onSelect,
    }),
    ...(canAccessOrganizationSettings
      ? [
          {
            key: 'organization-settings',
            title: i18n.t('navigation.organizationSettings', {
              ns: 'navigation',
            }),
            icon: 'UserCog' as const,
            url: currentOrganizationId
              ? getRoutePath('ORG_SETTINGS', { orgId: currentOrganizationId })
              : undefined,
            disabled: !currentOrganizationId,
            onClick: onSelect,
          },
        ]
      : []),
    {
      key: 'workspace-settings',
      title: i18n.t('settings.titles.workspaceSettings', { ns: 'settings' }),
      icon: 'Settings2',
      url:
        currentWorkspaceId && currentOrganizationId
          ? getRoutePath('WORKSPACE_SETTINGS', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
            })
          : undefined,
      disabled: !currentWorkspaceId || !currentOrganizationId,
      onClick: onSelect,
    },
    {
      key: 'api-keys',
      title: i18n.t('apiKeys.title', { ns: 'api-keys' }),
      icon: 'KeyRound',
      url: currentOrganizationId
        ? getRoutePath('API_KEYS', { orgId: currentOrganizationId })
        : undefined,
      disabled: !currentOrganizationId,
      onClick: onSelect,
    },
    ...(currentOrganizationId && currentWorkspaceId
      ? [
          {
            key: 'remote-connections',
            title: i18n.t('navigation.remoteConnections', { ns: 'navigation' }),
            icon: 'GlobeLock' as const,
            url: getRoutePath('REMOTE_CONNECTIONS', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
            }),
            onClick: onSelect,
          },
        ]
      : []),
    {
      key: 'separator-2',
      type: 'separator',
    },
    {
      key: 'documentation',
      title: i18n.t('navigation.documentation', { ns: 'navigation' }),
      icon: 'BookOpen',
      isExternalUrl: true,
      url: DOCUMENTATION_URL,
      onClick: onSelect,
    },
    {
      key: 'help',
      title: i18n.t('navigation.help', { ns: 'navigation' }),
      icon: 'CircleHelp',
      disabled: !isHelpConfigured,
      onClick: () => {
        openHelp();
        onSelect?.();
      },
    },
    {
      key: 'separator-3',
      type: 'separator',
    },
    {
      key: 'profile',
      title: i18n.t('navigation.profile', { ns: 'navigation' }),
      icon: 'UserRound',
      url: getRoutePath('USER_SETTINGS'),
      disabled: isCurrentUserError,
      onClick: onSelect,
    },
    {
      key: 'logout',
      title: i18n.t('navigation.logOut', { ns: 'navigation' }),
      icon: 'LogOut',
      onClick: () => {
        logout();
        onSelect?.();
      },
    },
    {
      key: 'separator-4',
      type: 'separator',
    },
    {
      key: 'switch-organization',
      title: i18n.t('navigation.switchOrganization', { ns: 'navigation' }),
      onClick: onSelect,
      disabled: isCurrentUserError,
    },
  ];
};
